import type { ReactNode } from 'react';

import { useGetIapUserQuery } from '@/services/customizationApi';
import { laravelOwnsData } from '@/lib/dataOwner';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { UserBgShape } from './UserBgShape';

interface UserHeaderBannerProps {
  /** QLASH user id — used to resolve the IAP user record + selected banner. */
  userId: number | string | undefined | null;
  /** Content laid over the banner, vertically centred (name, options, …). */
  children?: ReactNode;
  /** When provided, the whole banner becomes a button that fires this on tap. */
  onPress?: () => void;
  /** Accessible label for the tap target when interactive. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Esports-style profile header — chevron-clipped SVG, cyan stroke, with the
 * player's selected banner image (or the brand gradient) masked inside.
 *
 * It no longer draws an avatar. The Laravel picture circle it used to show is
 * legacy: the player's avatar is their AI-generated QAvatar, painted over the
 * banner by `<BannerAvatar />`. This component only owns the banner surface —
 * what sits on top is the caller's business.
 */
export function UserHeaderBanner({
  userId,
  children,
  onPress,
  ariaLabel,
  className,
}: UserHeaderBannerProps) {
  // ⚠️ `skip: userId == null` NON basta su una build Limulo, ed e' il difetto piu' caro
  // misurato il 2026-09-05: li' `userId` e' un **ObjectId**, quindi non e' null, quindi la
  // query parte — e paga DUE volte.
  //
  // 🔑 `v2-api` (customization/IAP) e' un TERZO servizio che si autentica col token
  // **Laravel**: il suo `prepareHeaders` chiama `getApiAccessToken()`, che con il monolite
  // spento non popola mai la cache ⇒ **ogni montaggio del banner rifa' l'exchange**. E il
  // banner e' montato su OGNI schermata. Misurato da or45 su uno sweep di 10 rotte:
  // **12 `exchange-token` 503 e 12 `customization/.../<ObjectId>` 500, correlati 1:1**.
  // Non erano due difetti: erano lo stesso, contato due volte.
  //
  // ⇒ La rinuncia e' dichiarata: su una build Limulo il banner personalizzato non si
  // risolve e resta la forma di default. Non si perde nulla che funzionasse — quella
  // chiamata rispondeva 500 comunque, perche' `v2-api` e' indicizzato sull'id **numerico**
  // di Laravel e l'ObjectId non e' una chiave che conosce.
  const { data: iapUser } = useGetIapUserQuery(userId ?? '', {
    skip: userId == null || !laravelOwnsData(),
  });

  const banner = iapUser?.banner;
  const bgUrl = banner?.media_uri ? cdnImage(banner.media_uri) : null;

  const interactive = !!onPress;

  const inner = (
    <>
      <UserBgShape imageUrl={bgUrl} className="absolute inset-0 h-full w-full" />
      <div className="absolute inset-0 flex items-center">{children}</div>
    </>
  );

  const containerClass = cn(
    // Always fills its wrapper; AppBanner owns the width (full, or 60% when the
    // banner is compact) so the avatar — sized off this box — follows either way.
    'relative w-full',
    interactive && 'transition-opacity active:opacity-80',
    className,
  );
  const style = { aspectRatio: '395 / 99' } as const;

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onPress}
        aria-label={ariaLabel}
        className={cn(containerClass, 'text-left')}
        style={style}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={containerClass} style={style}>
      {inner}
    </div>
  );
}
